import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Plus } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const mockWatchlist = [
  {
    ticker: "TSLA",
    name: "Tesla",
    price: 242.50,
    change: 5.2,
    sentiment: "Bullish",
    volume: "98.5M",
    data: Array.from({ length: 30 }, (_, i) => ({
      day: i,
      value: 230 + Math.sin(i / 5) * 15 + Math.random() * 5,
    })),
  },
  {
    ticker: "NVDA",
    name: "NVIDIA",
    price: 142.80,
    change: -2.1,
    sentiment: "Neutral",
    volume: "42.1M",
    data: Array.from({ length: 30 }, (_, i) => ({
      day: i,
      value: 145 + Math.sin(i / 4) * 8 - Math.random() * 3,
    })),
  },
  {
    ticker: "COIN",
    name: "Coinbase",
    price: 168.90,
    change: 8.7,
    sentiment: "Very Bullish",
    volume: "31.2M",
    data: Array.from({ length: 30 }, (_, i) => ({
      day: i,
      value: 155 + i * 0.45 + Math.random() * 8,
    })),
  },
];

const mockSignals = [
  {
    id: 1,
    ticker: "TSLA",
    signal: "Price crossed $240 resistance",
    catalyst: "Q4 Earnings Beat",
    impact: "High",
    time: "2 hours ago",
  },
  {
    id: 2,
    ticker: "COIN",
    signal: "Sentiment spike +45%",
    catalyst: "Bitcoin Rally",
    impact: "Medium",
    time: "1 hour ago",
  },
  {
    id: 3,
    ticker: "NVDA",
    signal: "Volume decline -32%",
    catalyst: "End of Quarter",
    impact: "Low",
    time: "30 mins ago",
  },
];

export default function Markets() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] },
    },
  };

  return (
    <div className="space-y-8">
      {/* Watchlist */}
      <motion.div variants={itemVariants}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-medium">Your Watchlist</h2>
          <Button className="bg-foreground text-background hover:bg-foreground/90">
            <Plus className="h-4 w-4 mr-2" />
            Add Ticker
          </Button>
        </div>

        <motion.div
          className="space-y-4"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {mockWatchlist.map((ticker, idx) => (
            <motion.div
              key={idx}
              variants={itemVariants}
              className="border border-border p-6 hover:border-foreground/50 transition-colors"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                {/* Left: Ticker Info */}
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-1">
                    {ticker.ticker}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">{ticker.name}</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-semibold text-foreground">
                      ${ticker.price}
                    </span>
                    <Badge
                      variant={ticker.change > 0 ? "default" : "destructive"}
                      className="flex items-center gap-1"
                    >
                      {ticker.change > 0 ? (
                        <TrendingUp className="h-3 w-3" />
                      ) : (
                        <TrendingDown className="h-3 w-3" />
                      )}
                      {Math.abs(ticker.change)}%
                    </Badge>
                  </div>
                </div>

                {/* Middle: Chart */}
                <div className="md:col-span-2">
                  <ResponsiveContainer width="100%" height={150}>
                    <LineChart data={ticker.data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                      <XAxis dataKey="day" hide />
                      <YAxis hide domain={["dataMin - 5", "dataMax + 5"]} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "var(--color-background)",
                          border: "1px solid var(--color-border)",
                          borderRadius: "6px",
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="currentColor"
                        dot={false}
                        isAnimationActive={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 pt-4 border-t border-border">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Sentiment</p>
                  <Badge variant="outline" className="text-xs">
                    {ticker.sentiment}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Volume</p>
                  <p className="text-sm font-medium text-foreground">
                    {ticker.volume}
                  </p>
                </div>
                <div className="text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-border"
                  >
                    View Details
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      {/* Market Signals */}
      <motion.div variants={itemVariants}>
        <h2 className="text-2xl font-medium mb-6">Market Signals</h2>
        <motion.div
          className="space-y-4"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {mockSignals.map((signal) => (
            <motion.div
              key={signal.id}
              variants={itemVariants}
              className="border border-border p-6 hover:border-foreground/50 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline" className="text-xs font-mono">
                      {signal.ticker}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {signal.time}
                    </span>
                  </div>
                  <h3 className="text-lg font-medium text-foreground mb-1">
                    {signal.signal}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Catalyst: {signal.catalyst}
                  </p>
                </div>
                <Badge
                  variant={
                    signal.impact === "High"
                      ? "destructive"
                      : signal.impact === "Medium"
                      ? "secondary"
                      : "outline"
                  }
                >
                  {signal.impact}
                </Badge>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </div>
  );
}
